import { Loader2, ShieldAlert } from "lucide-react";
import type { ReactNode } from "react";
import { Navigate } from "react-router-dom";

import { TwoFactorCard } from "@/features/auth/components/TwoFactorCard";
import { useMe } from "@/features/auth/hooks";
import { useAuthStore } from "@/features/auth/store";

/**
 * Réserve la console éditeur (/editeur) au personnel GoldenTech
 * (`is_platform_staff`). Même logique de lecture /me que PermissionGuard pour
 * éviter un faux 403 au rafraîchissement.
 */
export function PlatformGuard({ children }: { children: ReactNode }) {
  const meQuery = useMe();
  const storeMe = useAuthStore((s) => s.me);
  const me = meQuery.data ?? storeMe;

  if (meQuery.isLoading && !storeMe) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  // Backend : gestion éditeur = is_platform_staff OU superuser (cf.
  // IsSuperUserOrSafeAccessible / IsPlatformStaff). On aligne le guard dessus.
  if (!me?.is_platform_staff && !me?.is_superuser) {
    return <Navigate to="/403" replace />;
  }

  // C3 (socle) : mot de passe temporaire → changement forcé avant la console.
  // Le backend refuse de toute façon /api/v1/platform/* (403
  // auth.password_change_required) tant que la rotation n'est pas faite.
  if (me.must_change_password) {
    return <Navigate to="/profil/password" replace />;
  }

  // C3 (socle) : MFA obligatoire pour la console éditeur. Écran d'enrôlement
  // forcé (le backend répond 403 auth.mfa_enrollment_required tant que la 2FA
  // n'est pas active). Après activation, /me est rafraîchi et la console
  // s'affiche.
  if (!me.mfa_enabled) {
    return (
      <div className="mx-auto max-w-2xl space-y-4 px-4 py-10">
        <div className="flex items-start gap-3">
          <ShieldAlert className="mt-0.5 h-6 w-6 shrink-0 text-warning" />
          <div className="space-y-1">
            <h1 className="text-xl font-semibold">Double authentification requise</h1>
            <p className="text-sm text-muted-foreground">
              La console éditeur exige la double authentification (2FA) pour tout
              le personnel GoldenTech. Activez-la ci-dessous — l'accès est
              débloqué immédiatement après la confirmation du code.
            </p>
          </div>
        </div>
        <TwoFactorCard />
      </div>
    );
  }

  return <>{children}</>;
}
